import { AuthModalWrapper } from "../../../Auth/AuthModal";
import { usePrivatePDWrapperController } from "../hooks/usePrivatePropertyControl";
import { PDAuctionDetailsHeader } from "../../../propertyDetail/v2Components/PDAuctionDetailsHeader";
import { PropertyDetailGridView } from "../../../propertyDetail/v2Components/PropertyDetailGridView";
import { usePrivatePDAuctionDetails } from "../hooks/usePrivatePDAuctionDetails";
import { PDAcknowledgmentModal } from "../../../propertyDetail/v2Components/PDAcknowledgmentModal";
import { PDPropertyDescription } from "../../../propertyDetail/v2Components/PDPropertyDescription";
import { useMemo } from "react";
import { PDKnowMoreAboutPropertyCardV2 } from "../../../propertyDetail/v2Components/PDKnowMoreAboutPropertyCardV2";
import { PDMapViewV2 } from "../../../propertyDetail/v2Components/PDMapViewV2";
import {
  ImageType,
  PropertyImagePreview,
} from "../../../propertyDetail/v2Components/PropertyImagePreview";
import { PropertyType } from "../../../propertyDetail/v2Components/PropertyDetailWrapper";
import {
  PDContactAuthorisedOfficer,
  PDDocumentsView,
} from "../../../propertyDetail/v2Components";
import { getWatermarkedImgixUrl } from "services";
import { getSellerTypeDisplay } from "../../../common/ShareModal/ShareModal";
import { maskSellerName } from "services";
import { getPrivatePropertyArea } from "../../listingPageV2/utils/transformPrivateProperties";

interface PrivatePropertyDetailWrapperProps {
  propertyData: any;
  propertyType: PropertyType;
}

export const PrivatePropertyDetailWrapper: React.FC<
  PrivatePropertyDetailWrapperProps
> = ({ propertyData, propertyType }) => {
  const {
    setAuthModelOpen,
    setAcknowledgementOpen,
    handleInterested,
    isLoggedIn,
    authModelOpen,
    acknowledgementOpen,
    errorMessage,
    setAcknowledgementDetails,
    coordinates,
  } = usePrivatePDWrapperController({ propertyData });
  const { auctionDetails } = usePrivatePDAuctionDetails({ propertyData });

  const propertyDetailsFullLink = useMemo(() => {
    const baseUrl = window.location.href.split("/").filter(Boolean)[1];
    return `https://${baseUrl}/private-properties/${propertyData?.listing_id}`;
  }, [propertyData?.listing_id]);

  return (
    <div className="bg-brand-rootBackgroundVariant1">
      <div className="space-y-4 2xl:mx-auto px-customPaddingMobile lg:px-customPadding 2xl:px-0 w-full 2xl:w-[1340px]">
        <PDAuctionDetailsHeader
          propertyData={propertyData}
          setAuthModelOpen={setAuthModelOpen}
          setAcknowledgementOpen={setAcknowledgementOpen}
          handleInterested={handleInterested}
          auctionDetails={auctionDetails}
          propertyDetailsFullLink={propertyDetailsFullLink}
          propertyType={propertyType}
        />

        <PropertyDetailGridView
          leftChildren={
            <LeftChildren
              propertyData={propertyData}
              setAuthModelOpen={setAuthModelOpen}
              setAcknowledgementOpen={setAcknowledgementOpen}
              setAcknowledgementDetails={setAcknowledgementDetails}
              propertyType={propertyType}
            />
          }
          rightChildren={
            <RightChildren
              coordinates={coordinates}
              propertyData={propertyData}
            />
          }
        />

        <AuthModalWrapper
          userLoggedin={isLoggedIn}
          authModelOpen={authModelOpen}
          setAuthModelOpen={setAuthModelOpen}
        />
        <PDAcknowledgmentModal
          acknowledgementOpen={acknowledgementOpen}
          setAcknowledgementOpen={setAcknowledgementOpen}
          errorMessage={errorMessage}
        />
      </div>
    </div>
  );
};

interface LeftChildrenProps {
  propertyData: any;
  setAuthModelOpen: (value: boolean) => void;
  setAcknowledgementOpen: (value: boolean) => void;
  setAcknowledgementDetails: (value: any) => void;
  propertyType: PropertyType;
}

const LeftChildren = ({
  propertyData,
  setAuthModelOpen,
  setAcknowledgementOpen,
  setAcknowledgementDetails,
  propertyType,
}: LeftChildrenProps) => {
  const { isLoggedIn } = usePrivatePDWrapperController({ propertyData });

  const details = useMemo(() => {
    const area = getPrivatePropertyArea(propertyData);

    propertyData.area = area ? area : propertyData?.area;

    propertyData.possession_type =
      propertyData?.posted_properties_raw_data?.constructionStatus?.value ===
      "ready_to_move"
        ? "Ready To Move"
        : propertyData?.posted_properties_raw_data?.possessionDate;
    const sellerName = isLoggedIn
      ? propertyData?.borrower_name || "NA"
      : maskSellerName(propertyData?.borrower_name);

    const ownerType = propertyData?.owner_type || "owner";
    const sellerType = getSellerTypeDisplay(ownerType);

    return [
      { label: "Seller Name", value: sellerName },
      { label: "Seller Type", value: sellerType },
      {
        label: "Type",
        value: `${propertyData?.sub_asset_type?.name}`,
      },
      {
        label: "Area",
        value: propertyData?.area || "NA",
      },
      { label: "Possession", value: propertyData?.possession_type || "NA" },
    ];
  }, [propertyData, isLoggedIn]);

  return (
    <>
      <PDPropertyDescription
        description={propertyData?.description}
        details={details}
      />
      <PDDocumentsView
        propertyData={propertyData}
        setAuthModelOpen={setAuthModelOpen}
        propertyType={propertyType}
      />
      <PDContactAuthorisedOfficer
        propertyData={propertyData}
        setAuthModelOpen={setAuthModelOpen}
        propertyType={propertyType}
        title="View Seller Details"
      />
      <PDKnowMoreAboutPropertyCardV2
        propertyData={propertyData}
        setAuthModelOpen={setAuthModelOpen}
        setAcknowledgementOpen={setAcknowledgementOpen}
        setAcknowledgementDetails={setAcknowledgementDetails}
      />
    </>
  );
};

interface RightChildrenProps {
  coordinates: {
    isCoordinatesFound: boolean;
    value: string[];
  };
  propertyData: any;
}

const RightChildren = ({ coordinates, propertyData }: RightChildrenProps) => {
  const sliderImages = useMemo<ImageType[]>(() => {
    const images =
      propertyData?.posted_properties_data?.property_photos ||
      propertyData?.posted_properties_raw_data?.property_photos;
    return (
      images?.map((img: any) => {
        return {
          filePath: getWatermarkedImgixUrl(
            `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${img?.id}`,
          ),
          fileName: img?.filename,
        };
      }) || []
    );
  }, [
    propertyData?.posted_properties_data?.property_photos,
    propertyData?.posted_properties_raw_data?.property_photos,
  ]);
  return (
    <>
      <PDMapViewV2
        isCoordinateFound={coordinates.isCoordinatesFound}
        position={coordinates.value}
        locality={{
          isFound: propertyData?.locality?.name ? true : false,
          value: propertyData?.locality?.name
            ? propertyData?.locality?.name
            : null,
        }}
        city={propertyData?.city}
        state={propertyData?.state}
        area={propertyData?.area}
      />
      <PropertyImagePreview sliderImages={sliderImages} />
    </>
  );
};
